import { useState } from "react";
import { X, ThumbsDown, Send } from "lucide-react";
import { submitFeedback } from "@/lib/api";
import { DETECTED } from "@/constants/testIds";
import { toast } from "sonner";

const TYPES = [
  { key: "wrong_category", label: "Wrong category", needs: "category" },
  { key: "wrong_child", label: "Wrong child", needs: "child_name" },
  { key: "wrong_priority", label: "Wrong priority", needs: "priority" },
  { key: "wrong_due_date", label: "Wrong due date", needs: "due_date" },
  { key: "wrong_event", label: "Wrong event date / location", needs: "event_date" },
  { key: "missed_extraction", label: "Missed something important", needs: "missed_field" },
  { key: "other", label: "Other", needs: null },
];

const CATEGORIES = ["School", "Sports", "Camp", "Activity", "Appointment", "Payment", "Form", "Transportation", "Event", "General Child Update"];
const PRIORITIES = ["Critical", "Important", "FYI"];

export default function FeedbackDialog({ email, onClose, onSubmitted }) {
  const [selected, setSelected] = useState([]);
  const [correction, setCorrection] = useState({});
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const toggle = (key) => setSelected(s => s.includes(key) ? s.filter(k => k !== key) : [...s, key]);

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0 && !notes.trim()) {
      toast.error("Pick at least one reason or add a note.");
      return;
    }
    setLoading(true);
    try {
      await submitFeedback({
        email_id: email.id,
        verdict: "incorrect",
        feedback_types: selected,
        correction_data: correction,
        notes,
      });
      toast.success("Thanks — you just trained KidHQ.");
      onSubmitted();
    } catch (err) {
      toast.error("Could not save feedback.");
    } finally {
      setLoading(false);
    }
  };

  const showField = (k) => selected.some(s => (TYPES.find(t => t.key === s)?.needs) === k);

  return (
    <div className="fixed inset-0 z-50 bg-[var(--fcc-ink)]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid={DETECTED.feedbackDialog} onClick={e => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-8 sm:p-10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="fcc-pill bg-[var(--fcc-red-soft)] text-[#8B241D]"><ThumbsDown className="w-3 h-3" /> What did KidHQ get wrong?</span>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-3 leading-snug">{email.subject}</h2>
              <p className="text-xs text-[var(--fcc-muted)] mt-1">classified as {email.category} · {email.priority} · {email.child_name}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--fcc-cream-2)] rounded-xl"><X className="w-4 h-4 text-[var(--fcc-ink-soft)]" /></button>
          </div>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <fieldset className="grid sm:grid-cols-2 gap-2">
              {TYPES.map(t => (
                <label key={t.key} data-testid={`${DETECTED.feedbackType}-${t.key}`} className={`flex items-start gap-3 border rounded-2xl p-3 cursor-pointer transition ${selected.includes(t.key) ? "border-[var(--fcc-ink)] bg-[var(--fcc-cream-2)]" : "border-[var(--fcc-border)] bg-white hover:bg-[var(--fcc-cream-2)]"}`}>
                  <input type="checkbox" className="mt-1 accent-[var(--fcc-green)]" checked={selected.includes(t.key)} onChange={() => toggle(t.key)} />
                  <span className="text-sm font-semibold text-[var(--fcc-ink)]">{t.label}</span>
                </label>
              ))}
            </fieldset>

            {(showField("category") || showField("priority") || showField("child_name") || showField("due_date") || showField("event_date") || showField("missed_field")) && (
              <div className="rounded-2xl border border-[var(--fcc-border)] bg-[var(--fcc-cream-2)] p-4 space-y-3">
                <p className="label-overline">What should it be?</p>
                {showField("category") && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Correct category</label>
                    <select className="fcc-input mt-1" value={correction.category || ""} onChange={e => setCorrection({ ...correction, category: e.target.value })}>
                      <option value="">Choose…</option>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {showField("priority") && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Correct priority</label>
                    <select className="fcc-input mt-1" value={correction.priority || ""} onChange={e => setCorrection({ ...correction, priority: e.target.value })}>
                      <option value="">Choose…</option>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                )}
                {showField("child_name") && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Correct child name</label>
                    <input className="fcc-input mt-1" value={correction.child_name || ""} onChange={e => setCorrection({ ...correction, child_name: e.target.value })} placeholder="e.g. Emma" />
                  </div>
                )}
                {showField("due_date") && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Correct due date</label>
                    <input type="datetime-local" className="fcc-input mt-1" value={correction.due_date || ""} onChange={e => setCorrection({ ...correction, due_date: e.target.value })} />
                  </div>
                )}
                {showField("event_date") && (
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Correct event date</label>
                      <input type="datetime-local" className="fcc-input mt-1" value={correction.event_date || ""} onChange={e => setCorrection({ ...correction, event_date: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Correct location</label>
                      <input className="fcc-input mt-1" value={correction.location || ""} onChange={e => setCorrection({ ...correction, location: e.target.value })} />
                    </div>
                  </div>
                )}
                {showField("missed_field") && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">What did we miss? (comma-separated keys: payment_amount, required_items, …)</label>
                    <input className="fcc-input mt-1" value={correction.missed_field || ""} onChange={e => setCorrection({ ...correction, missed_field: e.target.value })} placeholder="payment_amount, required_items" />
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Notes (optional)</label>
              <textarea data-testid={DETECTED.feedbackNotes} className="fcc-input mt-1 min-h-[80px] font-body" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything else KidHQ should learn from this email…" />
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="fcc-btn-outline text-sm py-2.5 px-4">Cancel</button>
              <button data-testid={DETECTED.feedbackSubmit} disabled={loading} className="fcc-btn-primary text-sm py-2.5 px-4">
                <Send className="w-4 h-4" /> {loading ? "Saving…" : "Submit correction"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
